import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase, type VideoSubmission, type CreatorProfile } from '../../lib/supabase';

type MobileTab = 'home' | 'leaderboard' | 'submit' | 'profile';

export default function CreatorDashboard() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<CreatorProfile | null>(null);
  const [videos, setVideos] = useState<VideoSubmission[]>([]);
  const [allCreators, setAllCreators] = useState<CreatorProfile[]>([]);
  const [allVideos, setAllVideos] = useState<VideoSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<VideoSubmission | null>(null);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [selectedCreatorVideos, setSelectedCreatorVideos] = useState<{
    creator: CreatorProfile;
    videos: VideoSubmission[];
  } | null>(null);
  const [mobileTab, setMobileTab] = useState<MobileTab>('home');
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/auth/login');
      return;
    }

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const loadData = async () => {
    if (!user) return;

    try {
      setError(null);

      const { data: profileData, error: profileError } = await supabase
        .from('creator_profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      if (!profileData && !profileError) {
        const { data: newProfile, error: createError } = await supabase
          .from('creator_profiles')
          .insert({
            id: user.id,
            name:
              user.user_metadata?.name ||
              user.email?.split('@')[0] ||
              '創作者',
            email: user.email || '',
            total_videos: 0,
            total_rewards: 0,
          })
          .select()
          .single();

        if (createError) {
          console.error('Error creating profile:', createError);
        } else if (newProfile) {
          setProfile(newProfile);
        }
      } else if (profileError) {
        console.error('Error loading profile:', profileError);
      } else if (profileData) {
        setProfile(profileData);
      }

      const { data: videosData, error: videosError } = await supabase
        .from('video_submissions')
        .select('*')
        .eq('creator_id', user.id)
        .order('created_at', { ascending: false });

      if (videosError) {
        console.error('Error loading videos:', videosError);
      } else if (videosData) {
        setVideos(videosData);
      }

      const { data: creatorsData, error: creatorsError } = await supabase
        .from('creator_profiles')
        .select('*')
        .order('total_rewards', { ascending: false });

      if (creatorsError) {
        console.error('Error loading creators:', creatorsError);
      } else if (creatorsData) {
        setAllCreators(creatorsData);
      }

      const { data: allVideosData, error: allVideosError } = await supabase
        .from('video_submissions')
        .select('*')
        .eq('status', 'approved')
        .order('created_at', { ascending: false });

      if (allVideosError) {
        console.error('Error loading all videos:', allVideosError);
      } else if (allVideosData) {
        setAllVideos(allVideosData);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('載入資料失敗，請重新整理頁面');
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const getMyRank = () => {
    if (!profile) return null;
    const index = allCreators.findIndex((c) => c.id === profile.id);
    return index >= 0 ? index + 1 : null;
  };

  const getRankBadge = (rank: number) => {
    if (rank === 1) {
      return (
        <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full shadow-lg">
          <i className="ri-trophy-fill text-xl text-white"></i>
        </div>
      );
    }
    if (rank === 2) {
      return (
        <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-slate-300 to-slate-400 rounded-full shadow-lg">
          <i className="ri-medal-fill text-xl text-white"></i>
        </div>
      );
    }
    if (rank === 3) {
      return (
        <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-orange-400 to-orange-600 rounded-full shadow-lg">
          <i className="ri-medal-fill text-xl text-white"></i>
        </div>
      );
    }
    return (
      <div className="w-10 h-10 flex items-center justify-center bg-slate-700 rounded-full">
        <span className="text-slate-300 font-bold text-sm">#{rank}</span>
      </div>
    );
  };

  const getRankBadgeSmall = (rank: number) => {
    if (rank === 1) {
      return (
        <div className="w-8 h-8 flex items-center justify-center bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full shadow-lg">
          <i className="ri-trophy-fill text-base text-white"></i>
        </div>
      );
    }
    if (rank === 2) {
      return (
        <div className="w-8 h-8 flex items-center justify-center bg-gradient-to-br from-slate-300 to-slate-400 rounded-full shadow-lg">
          <i className="ri-medal-fill text-base text-white"></i>
        </div>
      );
    }
    if (rank === 3) {
      return (
        <div className="w-8 h-8 flex items-center justify-center bg-gradient-to-br from-orange-400 to-orange-600 rounded-full shadow-lg">
          <i className="ri-medal-fill text-base text-white"></i>
        </div>
      );
    }
    return (
      <div className="w-8 h-8 flex items-center justify-center bg-slate-700 rounded-full">
        <span className="text-slate-300 font-bold text-xs">#{rank}</span>
      </div>
    );
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      pending: {
        text: '審核中',
        color:
          'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
      },
      approved: {
        text: '已通過',
        color:
          'bg-teal-500/20 text-teal-300 border-teal-500/30',
      },
      rejected: {
        text: '未通過',
        color: 'bg-red-500/20 text-red-300 border-red-500/30',
      },
    };
    const badge = badges[status as keyof typeof badges] ?? badges.pending;
    return (
      <span
        className={`px-3 py-1 rounded-full text-xs font-medium border ${badge.color}`}
      >
        {badge.text}
      </span>
    );
  };

  const getPlatformIcon = (platform: string) => {
    const icons = {
      instagram: 'ri-instagram-line',
      tiktok: 'ri-tiktok-line',
      youtube: 'ri-youtube-line',
    };
    return (icons as Record<string, string>)[platform] ?? 'ri-video-line';
  };

  const getEmbedUrl = (url: string, platform: string) => {
    try {
      if (platform === 'youtube') {
        const youtubeRegex =
          /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
        const match = url.match(youtubeRegex);
        if (match && match[1]) {
          return `https://www.youtube.com/embed/${match[1]}`;
        }
      } else if (platform === 'instagram') {
        return url;
      } else if (platform === 'tiktok') {
        const tiktokRegex = /tiktok\.com\/.*\/video\/(\d+)/;
        const match = url.match(tiktokRegex);
        if (match && match[1]) {
          return `https://www.tiktok.com/embed/v2/${match[1]}`;
        }
      }
    } catch (e) {
      console.error('Error parsing video URL:', e);
    }
    return null;
  };

  const openVideoModal = (video: VideoSubmission) => {
    setSelectedVideo(video);
  };

  const closeVideoModal = () => {
    setSelectedVideo(null);
  };

  const openCreatorVideos = (creator: CreatorProfile) => {
    const creatorVideos = allVideos.filter(
      (v) => v.creator_id === creator.id
    );
    setSelectedCreatorVideos({ creator, videos: creatorVideos });
  };

  const closeCreatorVideos = () => {
    setSelectedCreatorVideos(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">載入中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-6">
        <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-8 max-w-md w-full text-center">
          <i className="ri-error-warning-line text-5xl text-red-400 mb-4"></i>
          <h2 className="text-xl font-bold text-white mb-2">載入失敗</h2>
          <p className="text-red-300 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            重新整理
          </button>
        </div>
      </div>
    );
  }

  const myRank = getMyRank();

  // ===== 手機版排行榜內容 =====
  const MobileLeaderboard = () => (
    <div className="px-4 py-4 pb-24">
      <h2 className="text-lg font-bold text-white mb-4 flex items-center">
        <i className="ri-trophy-line text-xl text-yellow-400 mr-2"></i>
        創作者排行榜
      </h2>

      {/* 我的排名卡片 */}
      {myRank && (
        <div className="bg-gradient-to-r from-teal-500/20 to-teal-600/20 border border-teal-500/50 rounded-xl p-4 mb-4">
          <div className="flex items-center gap-3">
            {getRankBadgeSmall(myRank)}
            <div className="flex-1">
              <p className="text-slate-400 text-xs">我的排名</p>
              <p className="text-white text-xl font-bold">第 {myRank} 名</p>
            </div>
            {myRank <= 3 && (
              <span className="px-2 py-1 bg-yellow-500/20 text-yellow-300 text-xs font-medium rounded-full border border-yellow-500/30">
                🎉 前三名
              </span>
            )}
          </div>
        </div>
      )}

      <div className="space-y-3">
        {allCreators.map((creator, index) => {
          const rank = index + 1;
          const isMe = creator.id === profile?.id;
          const creatorVideos = allVideos.filter(
            (v) => v.creator_id === creator.id
          );
          const totalViews = creatorVideos.reduce(
            (sum, v) => sum + v.view_count,
            0
          );

          return (
            <div
              key={creator.id}
              className={`rounded-xl p-4 transition-all ${
                isMe
                  ? 'bg-gradient-to-r from-teal-500/20 to-teal-600/20 border-2 border-teal-500'
                  : 'bg-slate-800/80 border border-slate-700'
              }`}
            >
              <div className="flex items-center gap-3 mb-3">
                {getRankBadgeSmall(rank)}
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-bold text-sm truncate">
                    {creator.name}
                    {isMe && (
                      <span className="ml-1 text-teal-400 text-xs">
                        (我)
                      </span>
                    )}
                  </h3>
                  {creator.game_type && (
                    <p className="text-slate-400 text-xs truncate">
                      <i className="ri-gamepad-line mr-1"></i>
                      {creator.game_type}
                    </p>
                  )}
                </div>
                {rank <= 3 && (
                  <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-300 text-xs font-medium rounded-full border border-yellow-500/30 whitespace-nowrap">
                    TOP {rank}
                  </span>
                )}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1 text-xs">
                    <i className="ri-video-line text-purple-400"></i>
                    <span className="text-white font-medium">
                      {creator.total_videos}
                    </span>
                    <span className="text-slate-500">影片</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs">
                    <i className="ri-eye-line text-orange-400"></i>
                    <span className="text-white font-medium">
                      {totalViews >= 10000
                        ? `${(totalViews / 10000).toFixed(1)}萬`
                        : totalViews.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-xs">
                    <i className="ri-money-dollar-circle-line text-teal-400"></i>
                    <span className="text-teal-400 font-medium">
                      {creator.total_rewards >= 10000
                        ? `${(creator.total_rewards / 10000).toFixed(1)}萬`
                        : creator.total_rewards.toLocaleString()}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => openCreatorVideos(creator)}
                  className="w-8 h-8 flex items-center justify-center bg-teal-500/20 hover:bg-teal-500/30 text-teal-300 rounded-lg transition-colors cursor-pointer border border-teal-500/30"
                >
                  <i className="ri-play-circle-line text-base"></i>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ===== 手機版個人頁面 =====
  const MobileProfile = () => (
    <div className="px-4 py-4 pb-24">
      {/* 個人資訊卡片 */}
      <div className="bg-slate-800/80 rounded-xl border border-slate-700 p-6 mb-4 text-center">
        <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full">
          <i className="ri-user-line text-4xl text-white"></i>
        </div>
        <h2 className="text-white text-xl font-bold mb-1">{profile?.name}</h2>
        <p className="text-slate-400 text-sm mb-4">{user?.email}</p>
        {myRank && (
          <div className="inline-flex items-center gap-2 bg-teal-500/20 border border-teal-500/30 rounded-full px-4 py-2">
            <i className="ri-trophy-line text-teal-400"></i>
            <span className="text-teal-300 text-sm font-medium">
              排名第 {myRank} 名
            </span>
          </div>
        )}
      </div>

      {/* 數據統計 */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-slate-800/80 rounded-xl p-4 border border-slate-700 text-center">
          <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 bg-teal-500/20 rounded-lg">
            <i className="ri-video-line text-xl text-teal-400"></i>
          </div>
          <p className="text-white font-bold text-lg">
            {profile?.total_videos || 0}
          </p>
          <p className="text-slate-400 text-xs">影片</p>
        </div>
        <div className="bg-slate-800/80 rounded-xl p-4 border border-slate-700 text-center">
          <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 bg-teal-500/20 rounded-lg">
            <i className="ri-money-dollar-circle-line text-xl text-teal-400"></i>
          </div>
          <p className="text-white font-bold text-lg">
            {profile?.total_rewards?.toLocaleString() || 0}
          </p>
          <p className="text-slate-400 text-xs">獎金</p>
        </div>
        <div className="bg-slate-800/80 rounded-xl p-4 border border-slate-700 text-center">
          <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 bg-teal-500/20 rounded-lg">
            <i className="ri-eye-line text-xl text-teal-400"></i>
          </div>
          <p className="text-white font-bold text-lg">
            {videos.reduce((sum, v) => sum + v.view_count, 0).toLocaleString()}
          </p>
          <p className="text-slate-400 text-xs">觀看</p>
        </div>
      </div>

      {/* 銀行帳戶狀態 */}
      <div className="bg-slate-800/80 rounded-xl border border-slate-700 p-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-amber-500/20 rounded-lg">
              <i className="ri-bank-card-line text-xl text-amber-400"></i>
            </div>
            <div>
              <p className="text-white text-sm font-medium">銀行帳戶</p>
              {profile?.bank_account_number ? (
                <p className="text-teal-400 text-xs flex items-center gap-1">
                  <i className="ri-check-line"></i>
                  {profile.bank_name} ****{profile.bank_account_number.slice(-4)}
                </p>
              ) : (
                <p className="text-amber-400 text-xs flex items-center gap-1">
                  <i className="ri-error-warning-line"></i>
                  尚未設定，無法領取獎金
                </p>
              )}
            </div>
          </div>
          <button
            onClick={() => navigate('/creator/edit-profile')}
            className="w-8 h-8 flex items-center justify-center bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-pencil-line text-base text-slate-300"></i>
          </button>
        </div>
      </div>

      {/* 選單列表 */}
      <div className="bg-slate-800/80 rounded-xl border border-slate-700 overflow-hidden">
        <button
          onClick={() => navigate('/creator/edit-profile')}
          className="flex items-center justify-between px-5 py-4 border-b border-slate-700 hover:bg-slate-700/50 transition-colors cursor-pointer w-full"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-teal-500/20 rounded-lg">
              <i className="ri-edit-line text-base text-teal-400"></i>
            </div>
            <span className="text-white text-sm">編輯個人資料</span>
          </div>
          <i className="ri-arrow-right-s-line text-xl text-slate-500"></i>
        </button>
        <Link
          to="/"
          className="flex items-center justify-between px-5 py-4 border-b border-slate-700 hover:bg-slate-700/50 transition-colors cursor-pointer"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-slate-700 rounded-lg">
              <i className="ri-home-line text-base text-slate-300"></i>
            </div>
            <span className="text-white text-sm">回到首頁</span>
          </div>
          <i className="ri-arrow-right-s-line text-xl text-slate-500"></i>
        </Link>
        <button
          onClick={() => setShowLogoutConfirm(true)}
          className="flex items-center justify-between px-5 py-4 w-full hover:bg-slate-700/50 transition-colors cursor-pointer"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-red-500/20 rounded-lg">
              <i className="ri-logout-box-line text-base text-red-400"></i>
            </div>
            <span className="text-red-400 text-sm">登出帳號</span>
          </div>
          <i className="ri-arrow-right-s-line text-xl text-slate-500"></i>
        </button>
      </div>
    </div>
  );

  // ===== 手機版首頁內容 =====
  const MobileHome = () => (
    <div className="px-4 py-4 pb-24">
      {/* 歡迎區 */}
      <div className="mb-5">
        <p className="text-slate-400 text-sm">歡迎回來</p>
        <h1 className="text-white text-xl font-bold">{profile?.name}</h1>
      </div>

      {/* 統計卡片 */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="bg-slate-800/80 rounded-xl p-4 border border-slate-700 text-center">
          <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 bg-teal-500/20 rounded-lg">
            <i className="ri-video-line text-xl text-teal-400"></i>
          </div>
          <p className="text-white font-bold text-xl">
            {profile?.total_videos || 0}
          </p>
          <p className="text-slate-400 text-xs">總影片</p>
        </div>
        <div className="bg-slate-800/80 rounded-xl p-4 border border-slate-700 text-center">
          <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 bg-teal-500/20 rounded-lg">
            <i className="ri-money-dollar-circle-line text-xl text-teal-400"></i>
          </div>
          <p className="text-white font-bold text-xl">
            {profile?.total_rewards?.toLocaleString() || 0}
          </p>
          <p className="text-slate-400 text-xs">獎金</p>
        </div>
        <div className="bg-slate-800/80 rounded-xl p-4 border border-slate-700 text-center">
          <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 bg-teal-500/20 rounded-lg">
            <i className="ri-eye-line text-xl text-teal-400"></i>
          </div>
          <p className="text-white font-bold text-xl">
            {videos.reduce((sum, v) => sum + v.view_count, 0).toLocaleString()}
          </p>
          <p className="text-slate-400 text-xs">觀看</p>
        </div>
      </div>

      {/* 排名提示 */}
      {myRank && (
        <button
          onClick={() => setMobileTab('leaderboard')}
          className="w-full bg-gradient-to-r from-teal-500/20 to-teal-600/20 border border-teal-500/50 rounded-xl p-4 mb-5 flex items-center justify-between cursor-pointer"
        >
          <div className="flex items-center gap-3">
            {getRankBadgeSmall(myRank)}
            <div className="text-left">
              <p className="text-white font-bold">第 {myRank} 名</p>
              <p className="text-slate-400 text-xs">查看完整排行榜</p>
            </div>
          </div>
          <i className="ri-arrow-right-s-line text-xl text-teal-400"></i>
        </button>
      )}

      {/* 我的影片 */}
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-white font-bold text-base">我的影片</h2>
        <span className="text-slate-400 text-xs">{videos.length} 支影片</span>
      </div>

      {videos.length === 0 ? (
        <div className="bg-slate-800/80 rounded-xl border border-slate-700 p-8 text-center">
          <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 bg-slate-700/50 rounded-full">
            <i className="ri-video-line text-2xl text-slate-500"></i>
          </div>
          <p className="text-slate-400 text-sm mb-3">還沒有提交任何影片</p>
          <button
            onClick={() => navigate('/creator/submit-video')}
            className="inline-flex items-center text-teal-400 hover:text-teal-300 font-medium text-sm cursor-pointer whitespace-nowrap"
          >
            立即提交第一支影片
            <i className="ri-arrow-right-line ml-1"></i>
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {videos.map((video) => (
            <div
              key={video.id}
              className="bg-slate-800/80 rounded-xl border border-slate-700 overflow-hidden"
            >
              <div className="flex items-start gap-3 p-4">
                {/* 平台圖標 */}
                <button
                  onClick={() => {
                    if (video.platform === 'instagram') {
                      window.open(video.video_url, '_blank');
                    } else {
                      openVideoModal(video);
                    }
                  }}
                  className="flex-shrink-0 w-16 h-16 flex items-center justify-center bg-slate-700/50 rounded-lg cursor-pointer hover:bg-slate-700 transition-colors relative"
                >
                  <i
                    className={`${getPlatformIcon(
                      video.platform
                    )} text-2xl text-teal-400`}
                  ></i>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-7 h-7 flex items-center justify-center bg-black/40 rounded-full">
                      <i className="ri-play-fill text-sm text-white"></i>
                    </div>
                  </div>
                </button>

                {/* 影片資訊 */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h3 className="text-white font-medium text-sm line-clamp-2 flex-1">
                      {video.video_title}
                    </h3>
                    {getStatusBadge(video.status)}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-400">
                    <span className="bg-slate-700/50 px-2 py-0.5 rounded">
                      {video.game_type}
                    </span>
                    <span>{new Date(video.created_at).toLocaleDateString('zh-TW')}</span>
                  </div>
                  <div className="flex items-center justify-between mt-2 text-xs">
                    <div className="flex items-center text-slate-400">
                      <i className="ri-eye-line mr-1"></i>
                      {video.view_count.toLocaleString()}
                    </div>
                    <span className="text-teal-400 font-medium">
                      NT${video.reward_amount.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* ===== 手機版 Header（簡化） ===== */}
      <header className="md:hidden bg-slate-900/80 border-b border-slate-800 sticky top-0 z-40 backdrop-blur-sm">
        <div className="px-4 py-3 flex items-center justify-between">
          <Link to="/">
            <img
              src="https://public.readdy.ai/ai/img_res/43e5ec71-732c-47e6-8652-53c813fd533d.png"
              alt="Logo"
              className="h-7"
            />
          </Link>
          <div className="flex items-center gap-2">
            {mobileTab === 'home' && (
              <button
                onClick={() => navigate('/creator/submit-video')}
                className="w-9 h-9 flex items-center justify-center bg-teal-500 rounded-full cursor-pointer"
              >
                <i className="ri-add-line text-lg text-white"></i>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* ===== 電腦版 Header ===== */}
      <header className="hidden md:block bg-slate-900/50 border-b border-slate-800 sticky top-0 z-40 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <Link to="/">
            <img
              src="https://public.readdy.ai/ai/img_res/43e5ec71-732c-47e6-8652-53c813fd533d.png"
              alt="Logo"
              className="h-8 md:h-10"
            />
          </Link>
          <div className="flex items-center gap-2 md:gap-4">
            <span className="text-slate-300 text-xs md:text-sm">
              歡迎，{profile?.name}
            </span>
            <button
              onClick={() => navigate('/creator/edit-profile')}
              className="px-3 py-2 md:px-4 md:py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg text-xs md:text-sm transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-edit-line mr-1 md:mr-2"></i>
              編輯資料
            </button>
            <button
              onClick={handleSignOut}
              className="px-3 py-2 md:px-4 md:py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs md:text-sm transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-logout-box-line mr-1 md:mr-2"></i>
              登出
            </button>
          </div>
        </div>
      </header>

      {/* ===== 手機版內容區 ===== */}
      <div className="md:hidden">
        {mobileTab === 'home' && <MobileHome />}
        {mobileTab === 'leaderboard' && <MobileLeaderboard />}
        {mobileTab === 'submit' && null}
        {mobileTab === 'profile' && <MobileProfile />}
      </div>

      {/* ===== 電腦版內容區（原有不變） ===== */}
      <div className="hidden md:block max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
        {/* 個人資訊卡片 */}
        <div className="bg-slate-800/80 rounded-xl border border-slate-700 p-6 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-teal-500 to-teal-600 rounded-full">
                <i className="ri-user-line text-4xl text-white"></i>
              </div>
              <div>
                <h2 className="text-white text-2xl font-bold mb-1">{profile?.name}</h2>
                <p className="text-slate-400 text-sm mb-3">{user?.email}</p>
                <div className="flex items-center gap-4">
                  {myRank && (
                    <div className="inline-flex items-center gap-2 bg-teal-500/20 border border-teal-500/30 rounded-full px-4 py-2">
                      <i className="ri-trophy-line text-teal-400"></i>
                      <span className="text-teal-300 text-sm font-medium">
                        排名第 {myRank} 名
                      </span>
                    </div>
                  )}
                  {profile?.game_type && (
                    <div className="inline-flex items-center gap-2 bg-slate-700/50 rounded-full px-4 py-2">
                      <i className="ri-gamepad-line text-slate-400"></i>
                      <span className="text-slate-300 text-sm">
                        {profile.game_type}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => navigate('/creator/edit-profile')}
                className="px-6 py-3 bg-teal-500 hover:bg-teal-600 text-white rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-edit-line mr-2"></i>
                編輯個人資料
              </button>
              {profile?.bank_account_number ? (
                <div className="bg-slate-700/50 rounded-lg px-4 py-2 text-center">
                  <p className="text-slate-400 text-xs mb-1">銀行帳戶</p>
                  <p className="text-teal-400 text-sm font-medium flex items-center justify-center gap-1">
                    <i className="ri-check-line"></i>
                    {profile.bank_name} ****{profile.bank_account_number.slice(-4)}
                  </p>
                </div>
              ) : (
                <div className="bg-amber-500/20 border border-amber-500/30 rounded-lg px-4 py-2 text-center">
                  <p className="text-amber-400 text-xs flex items-center justify-center gap-1">
                    <i className="ri-error-warning-line"></i>
                    尚未設定銀行帳戶
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 我的排名卡片 */}
        {myRank && (
          <div className="bg-gradient-to-r from-teal-500/20 to-teal-600/20 border-2 border-teal-500/50 rounded-xl p-6 mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {getRankBadge(myRank)}
                <div>
                  <p className="text-slate-400 text-sm mb-1">我的排名</p>
                  <p className="text-white text-3xl font-bold">
                    第 {myRank} 名
                  </p>
                  {myRank <= 3 && (
                    <span className="inline-block mt-2 px-3 py-1 bg-yellow-500/20 text-yellow-300 text-xs font-medium rounded-full border border-yellow-500/30">
                      🎉 恭喜進入前三名！
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => setShowLeaderboard(!showLeaderboard)}
                className="px-6 py-3 bg-teal-500/20 hover:bg-teal-500/30 text-teal-300 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap border border-teal-500/30"
              >
                <i className="ri-trophy-line mr-2"></i>
                {showLeaderboard ? '隱藏排行榜' : '查看完整排行榜'}
              </button>
            </div>
          </div>
        )}

        {/* 排行榜 */}
        {showLeaderboard && (
          <div className="bg-slate-800/80 rounded-xl border border-slate-700 overflow-hidden mb-8">
            <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white flex items-center">
                <i className="ri-trophy-line text-2xl text-yellow-400 mr-3"></i>
                創作者排行榜
              </h2>
              <button
                onClick={() => setShowLeaderboard(false)}
                className="w-8 h-8 flex items-center justify-center hover:bg-slate-700 rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl text-slate-400"></i>
              </button>
            </div>

            <div className="p-6 max-h-[600px] overflow-y-auto">
              <div className="space-y-4">
                {allCreators.map((creator, index) => {
                  const rank = index + 1;
                  const isTopThree = rank <= 3;
                  const isMe = creator.id === profile?.id;
                  const creatorVideos = allVideos.filter(
                    (v) => v.creator_id === creator.id
                  );
                  const totalViews = creatorVideos.reduce(
                    (sum, v) => sum + v.view_count,
                    0
                  );

                  return (
                    <div
                      key={creator.id}
                      className={`flex items-center gap-4 p-4 rounded-xl transition-all ${
                        isMe
                          ? 'bg-gradient-to-r from-teal-500/30 to-teal-600/30 border-2 border-teal-500'
                          : isTopThree
                          ? 'bg-gradient-to-r from-slate-700/50 to-slate-800/50 border-2 border-teal-500/30'
                          : 'bg-slate-900/50 border border-slate-700'
                      }`}
                    >
                      <div className="flex-shrink-0">{getRankBadge(rank)}</div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-white font-bold text-lg">
                            {creator.name}
                            {isMe && (
                              <span className="ml-2 text-teal-400 text-sm">
                                (我)
                              </span>
                            )}
                          </h3>
                          {isTopThree && (
                            <span className="px-2 py-1 bg-yellow-500/20 text-yellow-300 text-xs font-medium rounded-full border border-yellow-500/30 whitespace-nowrap">
                              TOP {rank}
                            </span>
                          )}
                        </div>
                        {creator.game_type && (
                          <div className="flex items-center gap-2 text-sm text-slate-400">
                            <i className="ri-gamepad-line"></i>
                            <span>{creator.game_type}</span>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-6">
                        <div className="text-center">
                          <div className="w-12 h-12 flex items-center justify-center bg-purple-500/20 rounded-lg mb-1">
                            <i className="ri-video-line text-xl text-purple-400"></i>
                          </div>
                          <p className="text-white font-bold text-lg">
                            {creator.total_videos}
                          </p>
                          <p className="text-slate-400 text-xs">影片</p>
                        </div>

                        <div className="text-center">
                          <div className="w-12 h-12 flex items-center justify-center bg-orange-500/20 rounded-lg mb-1">
                            <i className="ri-eye-line text-xl text-orange-400"></i>
                          </div>
                          <p className="text-orange-400 font-bold text-lg">
                            {totalViews >= 10000
                              ? `${(totalViews / 10000).toFixed(1)}萬`
                              : totalViews.toLocaleString()}
                          </p>
                          <p className="text-slate-400 text-xs">觀看</p>
                        </div>

                        <div className="text-center">
                          <div className="w-12 h-12 flex items-center justify-center bg-teal-500/20 rounded-lg mb-1">
                            <i className="ri-money-dollar-circle-line text-xl text-teal-400"></i>
                          </div>
                          <p className="text-teal-400 font-bold text-lg">
                            {creator.total_rewards >= 10000
                              ? `${(creator.total_rewards / 10000).toFixed(1)}萬`
                              : creator.total_rewards.toLocaleString()}
                          </p>
                          <p className="text-slate-400 text-xs">獎金</p>
                        </div>
                      </div>

                      <div className="flex-shrink-0">
                        <button
                          onClick={() => openCreatorVideos(creator)}
                          className="px-4 py-2 bg-teal-500/20 hover:bg-teal-500/30 text-teal-300 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap border border-teal-500/30"
                        >
                          <i className="ri-play-circle-line mr-2"></i>
                          查看影片
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 mb-4 md:mb-12">
          <div className="bg-slate-800/80 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <div className="w-14 h-14 flex items-center justify-center bg-teal-500/20 rounded-lg">
                <i className="ri-video-line text-3xl text-teal-400"></i>
              </div>
              <div className="text-right">
                <p className="text-4xl md:text-3xl font-bold text-white">
                  {profile?.total_videos || 0}
                </p>
              </div>
            </div>
            <p className="text-slate-400 text-sm">總影片數</p>
          </div>

          <div className="bg-slate-800/80 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <div className="w-14 h-14 flex items-center justify-center bg-teal-500/20 rounded-lg">
                <i className="ri-money-dollar-circle-line text-3xl text-teal-400"></i>
              </div>
              <div className="text-right">
                <p className="text-4xl md:text-3xl font-bold text-white">
                  NT$
                  {profile?.total_rewards?.toLocaleString() || 0}
                </p>
              </div>
            </div>
            <p className="text-slate-400 text-sm">累計獎金</p>
          </div>

          <div className="bg-slate-800/80 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <div className="w-14 h-14 flex items-center justify-center bg-teal-500/20 rounded-lg">
                <i className="ri-eye-line text-3xl text-teal-400"></i>
              </div>
              <div className="text-right">
                <p className="text-4xl md:text-3xl font-bold text-white">
                  {videos.reduce((sum, v) => sum + v.view_count, 0).toLocaleString()}
                </p>
              </div>
            </div>
            <p className="text-slate-400 text-sm">總觀看數</p>
          </div>
        </div>

        {/* Actions */}
        <div className="mb-6 md:mb-8">
          <Link
            to="/creator/submit-video"
            className="inline-flex items-center bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 md:px-8 md:py-4 rounded-lg text-base md:text-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line text-xl mr-2"></i>
            提交新影片
          </Link>
        </div>

        {/* Videos List */}
        <div className="bg-slate-800/80 rounded-xl border border-slate-700 overflow-hidden">
          <div className="px-4 md:px-6 py-4 border-b border-slate-700">
            <h2 className="text-lg md:text-xl font-bold text-white">我的影片</h2>
          </div>

          {videos.length === 0 ? (
            <div className="p-8 md:p-12 text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-slate-700/50 rounded-full">
                <i className="ri-video-line text-3xl text-slate-500"></i>
              </div>
              <p className="text-slate-400 mb-4">還沒有提交任何影片</p>
              <Link
                to="/creator/submit-video"
                className="inline-flex items-center text-teal-400 hover:text-teal-300 font-medium cursor-pointer whitespace-nowrap"
              >
                立即提交您的第一支影片
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 p-4 md:p-6">
              {videos.map((video) => {
                const embedUrl = getEmbedUrl(video.video_url, video.platform);
                return (
                  <div
                    key={video.id}
                    className="bg-slate-900/50 rounded-xl border border-slate-700 overflow-hidden hover:border-teal-500/50 transition-all duration-300 group"
                  >
                    <div className="relative aspect-video bg-slate-800">
                      {video.platform === 'instagram' ? (
                        <a
                          href={video.video_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 hover:opacity-90 transition-opacity cursor-pointer group"
                        >
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="text-center">
                              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-3 bg-white/20 backdrop-blur-sm rounded-full border-3 border-white group-hover:scale-110 transition-transform">
                                <i className="ri-instagram-line text-5xl text-white"></i>
                              </div>
                              <p className="text-white font-medium text-sm">
                                點擊在 Instagram 觀看
                              </p>
                            </div>
                          </div>
                        </a>
                      ) : embedUrl ? (
                        <button
                          onClick={() => openVideoModal(video)}
                          className="w-full h-full flex items-center justify-center bg-slate-800 hover:bg-slate-700 transition-colors cursor-pointer group"
                        >
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-16 h-16 flex items-center justify-center bg-teal-500/20 rounded-full border-2 border-teal-500 group-hover:bg-teal-500/30 group-hover:scale-110 transition-all">
                              <i className="ri-play-fill text-3xl text-teal-400"></i>
                            </div>
                          </div>
                          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent opacity-60"></div>
                          <div className="absolute top-3 right-3">
                            <i
                              className={`${getPlatformIcon(video.platform)} text-2xl text-white drop-shadow-lg`}
                            ></i>
                          </div>
                        </button>
                      ) : (
                        <a
                          href={video.video_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full h-full flex items-center justify-center bg-slate-800 hover:bg-slate-700 transition-colors cursor-pointer"
                        >
                          <div className="text-center">
                            <i
                              className={`${getPlatformIcon(
                                video.platform
                              )} text-4xl text-teal-400 mb-2`}
                            ></i>
                            <p className="text-slate-400 text-sm">
                              點擊觀看影片
                            </p>
                          </div>
                        </a>
                      )}
                    </div>

                    <div className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="text-white font-medium text-sm line-clamp-2 flex-1 mr-2">
                          {video.video_title}
                        </h3>
                        {getStatusBadge(video.status)}
                      </div>

                      <div className="flex items-center text-xs text-slate-400 mb-3">
                        <span className="bg-slate-800 px-2 py-1 rounded">
                          {video.game_type}
                        </span>
                      </div>

                      <div className="flex items-center justify-between text-sm pt-3 border-t border-slate-800">
                        <div className="flex items-center text-slate-400">
                          <i className="ri-eye-line mr-1"></i>
                          <span>{video.view_count.toLocaleString()}</span>
                        </div>
                        <div className="text-teal-400 font-medium">
                          NT${video.reward_amount.toLocaleString()}
                        </div>
                      </div>

                      <div className="text-xs text-slate-500 mt-2">
                        {new Date(video.created_at).toLocaleDateString(
                          'zh-TW'
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ===== 手機版底部導航 ===== */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur-lg border-t border-slate-700 z-50">
        <div className="flex items-center justify-around py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
          <button
            onClick={() => setMobileTab('home')}
            className={`flex flex-col items-center gap-0.5 px-4 py-1.5 rounded-lg cursor-pointer transition-colors ${
              mobileTab === 'home' ? 'text-teal-400' : 'text-slate-500'
            }`}
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <i className={`${mobileTab === 'home' ? 'ri-home-fill' : 'ri-home-line'} text-xl`}></i>
            </div>
            <span className="text-xs font-medium">首頁</span>
          </button>

          <button
            onClick={() => setMobileTab('leaderboard')}
            className={`flex flex-col items-center gap-0.5 px-4 py-1.5 rounded-lg cursor-pointer transition-colors ${
              mobileTab === 'leaderboard' ? 'text-teal-400' : 'text-slate-500'
            }`}
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <i className={`${mobileTab === 'leaderboard' ? 'ri-trophy-fill' : 'ri-trophy-line'} text-xl`}></i>
            </div>
            <span className="text-xs font-medium">排行榜</span>
          </button>

          <button
            onClick={() => navigate('/creator/submit-video')}
            className="flex flex-col items-center gap-0.5 px-4 py-1.5 cursor-pointer"
          >
            <div className="w-10 h-10 flex items-center justify-center bg-teal-500 rounded-full shadow-lg shadow-teal-500/30 -mt-4">
              <i className="ri-add-line text-2xl text-white"></i>
            </div>
            <span className="text-xs font-medium text-teal-400">提交</span>
          </button>

          <button
            onClick={() => setMobileTab('profile')}
            className={`flex flex-col items-center gap-0.5 px-4 py-1.5 rounded-lg cursor-pointer transition-colors ${
              mobileTab === 'profile' ? 'text-teal-400' : 'text-slate-500'
            }`}
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <i className={`${mobileTab === 'profile' ? 'ri-user-fill' : 'ri-user-line'} text-xl`}></i>
            </div>
            <span className="text-xs font-medium">我的</span>
          </button>
        </div>
      </nav>

      {/* LINE@ 客服按鈕 */}
      <a
        href="https://lin.ee/pOxSsmV"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-24 md:bottom-6 right-6 z-40 w-14 h-14 bg-[#06C755] hover:bg-[#05b34c] rounded-full flex items-center justify-center shadow-2xl hover:shadow-[#06C755]/50 transition-all duration-300 hover:scale-110 cursor-pointer group"
        aria-label="LINE 客服"
      >
        <i className="ri-customer-service-2-fill text-2xl text-white group-hover:scale-110 transition-transform"></i>
        <div className="absolute -top-12 right-0 bg-slate-800 text-white text-xs px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-none">
          聯繫客服
          <div className="absolute bottom-0 right-6 transform translate-y-1/2 rotate-45 w-2 h-2 bg-slate-800"></div>
        </div>
      </a>

      {/* 登出確認彈窗 */}
      {showLogoutConfirm && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6"
          onClick={() => setShowLogoutConfirm(false)}
        >
          <div
            className="bg-slate-800 rounded-2xl p-6 w-full max-w-sm border border-slate-700"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 bg-red-500/20 rounded-full">
                <i className="ri-logout-box-line text-3xl text-red-400"></i>
              </div>
              <h3 className="text-white text-lg font-bold mb-2">
                確定要登出嗎？
              </h3>
              <p className="text-slate-400 text-sm">
                登出後需要重新登入才能使用
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="flex-1 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={handleSignOut}
                className="flex-1 py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                確定登出
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 影片播放 Modal */}
      {selectedVideo && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={closeVideoModal}
        >
          <div
            className="relative w-full max-w-5xl bg-slate-900 rounded-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeVideoModal}
              className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center bg-slate-800/80 hover:bg-slate-700 rounded-full text-white transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>

            <div className="aspect-video bg-black">
              {(() => {
                const embedUrl = getEmbedUrl(
                  selectedVideo.video_url,
                  selectedVideo.platform
                );

                if (selectedVideo.platform === 'instagram') {
                  return (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500">
                      <div className="text-center p-8">
                        <div className="w-24 h-24 flex items-center justify-center mx-auto mb-6 bg-white/20 backdrop-blur-sm rounded-full">
                          <i className="ri-instagram-line text-6xl text-white"></i>
                        </div>
                        <h3 className="text-white text-2xl font-bold mb-4">
                          Instagram 影片
                        </h3>
                        <p className="text-white/80 mb-6 max-w-md">
                          由於 Instagram 的限制，請點擊下方按鈕在 Instagram
                          應用程式或網頁中觀看完整影片
                        </p>
                        <a
                          href={selectedVideo.video_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center bg-white hover:bg-gray-100 text-purple-600 px-8 py-4 rounded-full font-bold text-lg transition-colors cursor-pointer whitespace-nowrap shadow-lg"
                        >
                          <i className="ri-instagram-line text-2xl mr-3"></i>
                          在 Instagram 觀看
                          <i className="ri-external-link-line ml-3 text-xl"></i>
                        </a>
                      </div>
                    </div>
                  );
                }

                if (embedUrl) {
                  return (
                    <iframe
                      src={embedUrl}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  );
                }

                return (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-center">
                      <i className="ri-error-warning-line text-5xl text-red-400 mb-4"></i>
                      <p className="text-white mb-4">無法載入影片</p>
                      <a
                        href={selectedVideo.video_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap"
                      >
                        在原平台觀看
                        <i className="ri-external-link-line ml-2"></i>
                      </a>
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="p-4 md:p-6 border-t border-slate-800">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h2 className="text-lg md:text-xl font-bold text-white mb-2">
                    {selectedVideo.video_title}
                  </h2>
                  <div className="flex flex-wrap items-center gap-3 text-sm text-slate-400">
                    <div className="flex items-center">
                      <i
                        className={`${getPlatformIcon(
                          selectedVideo.platform
                        )} text-lg mr-2 text-teal-400`}
                      ></i>
                      <span className="capitalize">
                        {selectedVideo.platform}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-gamepad-line mr-1"></i>
                      {selectedVideo.game_type}
                    </div>
                    <div className="flex items-center">
                      <i className="ri-eye-line mr-1"></i>
                      {selectedVideo.view_count.toLocaleString()} 次觀看
                    </div>
                  </div>
                </div>
                {getStatusBadge(selectedVideo.status)}
              </div>

              {selectedVideo.description && (
                <div className="mb-4">
                  <h3 className="text-white font-medium mb-2 text-sm">
                    影片描述
                  </h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    {selectedVideo.description}
                  </p>
                </div>
              )}

              <div className="flex items-center justify-between pt-4 border-t border-slate-800">
                <div className="text-slate-400 text-sm">
                  提交日期：{new Date(selectedVideo.created_at).toLocaleDateString('zh-TW')}
                </div>
                <div className="text-teal-400 font-bold text-lg">
                  獎金：NT${selectedVideo.reward_amount.toLocaleString()}
                </div>
              </div>

              {selectedVideo.admin_notes && (
                <div className="mt-4 bg-slate-800/50 rounded-lg p-4">
                  <h3 className="text-white font-medium mb-2 text-sm flex items-center">
                    <i className="ri-message-3-line mr-2"></i>
                    管理員備註
                  </h3>
                  <p className="text-slate-400 text-sm">
                    {selectedVideo.admin_notes}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 創作者影片列表 Modal */}
      {selectedCreatorVideos && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-2 md:p-4"
          onClick={closeCreatorVideos}
        >
          <div
            className="relative w-full max-w-6xl bg-slate-900 rounded-xl overflow-hidden max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 md:px-6 py-4 border-b border-slate-800 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3 md:gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-teal-500/20 rounded-full">
                  <i className="ri-user-line text-xl md:text-2xl text-teal-400"></i>
                </div>
                <div>
                  <h2 className="text-base md:text-xl font-bold text-white">
                    {selectedCreatorVideos.creator.name} 的影片
                  </h2>
                  <p className="text-slate-400 text-xs md:text-sm">
                    共 {selectedCreatorVideos.videos.length} 支影片
                  </p>
                </div>
              </div>
              <button
                onClick={closeCreatorVideos}
                className="w-10 h-10 flex items-center justify-center bg-slate-800/80 hover:bg-slate-700 rounded-full text-white transition-colors cursor-pointer flex-shrink-0"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="p-4 md:p-6 overflow-y-auto flex-1">
              {selectedCreatorVideos.videos.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-slate-800 rounded-full">
                    <i className="ri-video-line text-3xl text-slate-600"></i>
                  </div>
                  <p className="text-slate-400">
                    此創作者還沒有已通過的影片
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {selectedCreatorVideos.videos.map((video) => {
                    const embedUrl = getEmbedUrl(
                      video.video_url,
                      video.platform
                    );
                    return (
                      <div
                        key={video.id}
                        className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden hover:border-teal-500/50 transition-all duration-300 group"
                      >
                        <div className="relative aspect-video bg-slate-800">
                          {video.platform === 'instagram' ? (
                            <a
                              href={video.video_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 hover:opacity-90 transition-opacity cursor-pointer group"
                            >
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="text-center">
                                  <div className="w-16 h-16 flex items-center justify-center mx-auto mb-3 bg-white/20 backdrop-blur-sm rounded-full border-3 border-white group-hover:scale-110 transition-transform">
                                    <i className="ri-instagram-line text-5xl text-white"></i>
                                  </div>
                                  <p className="text-white font-medium text-sm">
                                    點擊在 Instagram 觀看
                                  </p>
                                </div>
                              </div>
                            </a>
                          ) : embedUrl ? (
                            <button
                              onClick={() => openVideoModal(video)}
                              className="w-full h-full flex items-center justify-center bg-slate-800 hover:bg-slate-700 transition-colors cursor-pointer group"
                            >
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-14 h-14 flex items-center justify-center bg-teal-500/20 rounded-full border-2 border-teal-500 group-hover:bg-teal-500/30 group-hover:scale-110 transition-all">
                                  <i className="ri-play-fill text-2xl text-teal-400"></i>
                                </div>
                              </div>
                              <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent opacity-60"></div>
                              <div className="absolute top-2 right-2">
                                <i
                                  className={`${getPlatformIcon(
                                    video.platform
                                  )} text-xl text-white drop-shadow-lg`}
                                ></i>
                              </div>
                            </button>
                          ) : (
                            <a
                              href={video.video_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-full h-full flex items-center justify-center bg-slate-800 hover:bg-slate-700 transition-colors cursor-pointer"
                            >
                              <div className="text-center">
                                <i
                                  className={`${getPlatformIcon(
                                    video.platform
                                  )} text-3xl text-teal-400 mb-2`}
                                ></i>
                                <p className="text-slate-400 text-sm">
                                  點擊觀看
                                </p>
                              </div>
                            </a>
                          )}
                        </div>

                        <div className="p-4">
                          <h3 className="text-white font-medium text-sm line-clamp-2 mb-2">
                            {video.video_title}
                          </h3>

                          <div className="flex items-center text-xs text-slate-400 mb-3">
                            <span className="bg-slate-700/50 px-2 py-1 rounded">
                              {video.game_type}
                            </span>
                          </div>

                          <div className="flex items-center justify-between text-sm pt-3 border-slate-700">
                            <div className="flex items-center text-slate-400">
                              <i className="ri-eye-line mr-1"></i>
                              <span>{video.view_count.toLocaleString()}</span>
                            </div>
                            <div className="text-teal-400 font-medium">
                              NT${video.reward_amount.toLocaleString()}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
